import React, { useCallback, useEffect, useState } from 'react';
import { listsViewStyles } from './styles';
import { withStyles } from '@material-ui/core/styles';
import OpenedModal from '../common/OpenedModal';
import { Accordion, AccordionDetails, AccordionSummary, Button, makeStyles, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClientTab from './tabs/ClientTab';
import CalendarTab from './tabs/CalendarTab';
import ListApi from '../../collums-components/api/ListApi';
import { modalsButtonStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';
import { useForm } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import CustomerFields from './tabs/CustomerFields';
import ListTab from './tabs/ListTab';
import OrganisationsApi from '../../api/OrganisationsApi';
import {
    CUSTOMER_DEFAULT_FIELDS,
    CUSTOMER_DEFAULT_FIELDS_FOR_CUSTOMER_APP,
    LEADS_DEFAULT_FIELDS,
    DEFAULT_ETHNICITY_OPTIONS,
    DEFAULT_STATUS_OPTIONS,
    MARKETING_DATA_FIELD_NAMES
} from '../../collums-constants';

const ListsView = () => {
    const [expanded, setExpanded] = useState('');
    const globalStyles = makeStyles(modalsButtonStyles)();
    const { register, handleSubmit } = useForm();
    const [formValues, setFormValues] = useState({});
    const [formInitialValues, setFormInitialValues] = useState({});
    const cloneFields = fields => fields.map(f => ({ ...f }));
    const mergeWithDefaults = (savedFields, defaultFields) => {
        const defaultsByName = Object.fromEntries(defaultFields.map(f => [f.name, f]));
        const merged = savedFields
            .filter(f => defaultsByName[f.name])
            .map(f => ({
                ...f,
                label: defaultsByName[f.name]?.label || f.label
            }));
        const savedNames = new Set(savedFields.map(f => f.name));
        const missingFields = defaultFields.filter(f => !savedNames.has(f.name));
        return [...merged, ...missingFields];
    };
    const customerDefaultFields = CUSTOMER_DEFAULT_FIELDS;
    const customerDefaultFieldsForCustomerApp = CUSTOMER_DEFAULT_FIELDS_FOR_CUSTOMER_APP;

    const [customerFields, setCustomerFields] = useState(cloneFields(customerDefaultFields));
    const [customerFieldsForCustomerApp, setCustomerFieldsForCustomerApp] = useState(
        cloneFields(customerDefaultFieldsForCustomerApp)
    );
    const [leadsFields, setLeadsFields] = useState(cloneFields(LEADS_DEFAULT_FIELDS));
    const [marketingDataEnabled, setMarketingDataEnabled] = useState(false);

    const filterMarketingFields = fields => {
        if (marketingDataEnabled) return fields;
        return fields.filter(f => !MARKETING_DATA_FIELD_NAMES.includes(f.name));
    };

    useEffect(() => {
        ListApi.getAll().then(lists => {
            const initialFormValues = {};
            for (let i = 0; i < lists.length; i++) {
                initialFormValues[lists[i].sys_name] = lists[i].values;
            }
            if (!initialFormValues.calendar_block_names) {
                initialFormValues.calendar_block_names = ['Meeting', '121'].join('\n');
            }
            if (!initialFormValues.calendar_break_names) {
                initialFormValues.calendar_break_names = ['Lunch', 'Tea break'].join('\n');
            }
            setFormValues(initialFormValues);
            setFormInitialValues(initialFormValues);
        });

        OrganisationsApi.getOrganisation().then(organisation => {
            setMarketingDataEnabled(!!organisation.marketingDataEnabled);
            if (organisation.customerFields && organisation.customerFields.length) {
                setCustomerFields(mergeWithDefaults(organisation.customerFields, CUSTOMER_DEFAULT_FIELDS));
            }
            if (organisation.customerFieldsForCustomerApp && organisation.customerFieldsForCustomerApp.length) {
                setCustomerFieldsForCustomerApp(
                    mergeWithDefaults(
                        organisation.customerFieldsForCustomerApp,
                        CUSTOMER_DEFAULT_FIELDS_FOR_CUSTOMER_APP
                    )
                );
            }
            if (organisation.leadsFields && organisation.leadsFields.length) {
                setLeadsFields(mergeWithDefaults(organisation.leadsFields, LEADS_DEFAULT_FIELDS));
            }
        });

        // eslint-disable-next-line
    }, []);

    const handleCancel = () => {
        setFormValues(formInitialValues);
    };

    const removeEmptyLines = value => {
        if (!value) return value;
        return value
            .split('\n')
            .filter(line => line.trim() !== '')
            .join('\n');
    };

    const onSubmit = useCallback(
        async values => {
            if (!values.client_referral_sources || !values.client_referral_sources.trim()) {
                toastr.error('Client referral sources cannot be empty');
                return;
            }
            const newValues = { ...values };
            newValues.calendar_block_names = removeEmptyLines(newValues.calendar_block_names);
            newValues.calendar_break_names = removeEmptyLines(newValues.calendar_break_names);
            newValues.client_ethnicity = removeEmptyLines(newValues.client_ethnicity);
            newValues.client_status = removeEmptyLines(newValues.client_status);

            if (!newValues.calendar_block_names || !newValues.calendar_break_names) {
                toastr.error('Cannot leave text field empty');
                return;
            }

            newValues.customerFields = customerFields;
            newValues.customerFieldsForCustomerApp = customerFieldsForCustomerApp;
            newValues.leadsFields = leadsFields;
            ListApi.updateOrganisation(newValues)
                .then(async () => {
                    toastr.success('Lists successfully updated!');
                })
                .catch(error => {
                    toastr.error(error?.data?.message || 'Something went wrong');
                });
        },
        [customerFields, customerFieldsForCustomerApp, leadsFields]
    );

    const submit = useCallback(
        e => {
            if (e) {
                e.preventDefault();

                handleSubmit(onSubmit)();
            }
        },
        [handleSubmit, onSubmit]
    );

    return (
        <OpenedModal>
            <form onSubmit={submit}>
                <Accordion
                    expanded={expanded === 'client_details'}
                    onChange={() => setExpanded(expanded === 'client_details' ? '' : 'client_details')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Client referral sources</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ClientTab register={register} formValues={formValues} setFormValues={setFormValues} />
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    expanded={expanded === 'client_ethnicity'}
                    onChange={() => setExpanded(expanded === 'client_ethnicity' ? '' : 'client_ethnicity')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Client ethnicity options</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ListTab
                            register={register}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            fieldName="client_ethnicity"
                            defaultValues={DEFAULT_ETHNICITY_OPTIONS}
                        />
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    expanded={expanded === 'client_status'}
                    onChange={() => setExpanded(expanded === 'client_status' ? '' : 'client_status')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Client status options</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ListTab
                            register={register}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            fieldName="client_status"
                            defaultValues={DEFAULT_STATUS_OPTIONS}
                        />
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    expanded={expanded === 'calendar'}
                    onChange={() => setExpanded(expanded === 'calendar' ? '' : 'calendar')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Calendar block and break names</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CalendarTab register={register} formValues={formValues} setFormValues={setFormValues} />
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    expanded={expanded === 'customer_fields_customer_app'}
                    onChange={() =>
                        setExpanded(expanded === 'customer_fields_customer_app' ? '' : 'customer_fields_customer_app')
                    }
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Choose fields for creating new clients online</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CustomerFields
                            fields={customerFieldsForCustomerApp}
                            setFields={setCustomerFieldsForCustomerApp}
                            description="Choose the fields you wish to be shown (and must be completed) when a client signs up as a new client online. Change the order by dragging the 6 dots to the left of each field."
                        />
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    expanded={expanded === 'customer_fields'}
                    onChange={() => setExpanded(expanded === 'customer_fields' ? '' : 'customer_fields')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Choose fields for creating new clients within Collums</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CustomerFields
                            fields={filterMarketingFields(customerFields)}
                            setFields={setCustomerFields}
                            description="Choose the fields you wish to be shown (and must be completed) when you create a new client within
                    Collums. Change the order by dragging the 6 dots to the left of each field."
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'leads_fields'}
                    onChange={() => setExpanded(expanded === 'leads_fields' ? '' : 'leads_fields')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Choose fields for creating new leads within Collums</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CustomerFields
                            fields={filterMarketingFields(leadsFields)}
                            setFields={setLeadsFields}
                            description="Choose the fields you wish to be shown (and must be completed) when you create a new lead within
                    Collums. Change the order by dragging the 6 dots to the left of each field."
                        />
                    </AccordionDetails>
                </Accordion>

                <div className={globalStyles.buttonsContainer}>
                    <Button className={globalStyles.cancelButton} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button className={globalStyles.confirmButton} type="submit" variant="outlined">
                        Save
                    </Button>
                </div>
            </form>
        </OpenedModal>
    );
};

export default withStyles(listsViewStyles)(ListsView);
